"use client";

import React, { useState } from "react";
import { api } from "~/trpc/react";
import Image from "next/image";
import { Link } from "~/i18n/navigation";
import { getPokemonUrl } from "~/lib/routing";
import { Card } from "~/components/ui/card";
import { ArrowRight, Gift, Search } from "lucide-react";
import { getPokemonImageSrc } from "~/lib/pokemon";
import { useTranslations, useLocale } from "next-intl";
import { resolveQuestUrlSlug, isValidSlug } from "~/lib/quest-slugs";

export interface QuestStat {
  title: string | null;
  quest_target: number | null;
  image_url: string | null;
  last_seen: number | null;
  reward_type: number | null;
  reward_pokemon_id: number | null;
  reward_pokemon_form_id: number | null;
  reward_pokemon_costume_id: number | null;
  reward_item_id: number | null;
  reward_item_amount: number | null;
  quest_count: number;
  translated_task: string;
  translated_reward: string;
  template: string | null;
  task_slug: string | null;
}

interface PageData {
  quests: QuestStat[];
  totalQuests: number;
}

function formatTimeAgo(
  timestamp: number | null,
  tCommon: (key: string, values?: Record<string, string | number>) => string,
) {
  if (!timestamp) return tCommon("never");
  const Math_floor = Math.floor;
  const now = Math_floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) return tCommon("justNow");
  if (diff < 3600) return tCommon("minutesAgo", { count: Math_floor(diff / 60) });
  if (diff < 86400) return tCommon("hoursAgo", { count: Math_floor(diff / 3600) });
  return tCommon("daysAgo", { count: Math_floor(diff / 86400) });
}

export default function QuestListClient({
  initialData,
}: {
  initialData: PageData;
}) {
  const locale = useLocale();
  const t = useTranslations("Quests");
  const tCommon = useTranslations("Common");

  const { data } = api.quest.getRecentQuests.useQuery(undefined, {
    initialData,
    refetchInterval: 600000,
    staleTime: 600000,
  });

  const [searchQuery, setSearchQuery] = useState("");

  const matchesSearch = (q: QuestStat) => {
    if (!searchQuery) return true;
    const s = searchQuery.toLowerCase();
    if (q.translated_task?.toLowerCase().includes(s)) return true;
    if (q.translated_reward?.toLowerCase().includes(s)) return true;
    
    return false;
  };

  const filteredData = data.quests.filter(matchesSearch);

  const pokemonQuests = filteredData.filter((q: QuestStat) => q.reward_type === 7);
  const stardustQuests = filteredData.filter((q: QuestStat) => q.reward_type === 3);
  
  const pokeballIds = [1, 2, 3, 4];
  const pokeballQuests = filteredData.filter(
    (q: QuestStat) => q.reward_type === 2 && q.reward_item_id && pokeballIds.includes(q.reward_item_id)
  );

  const berryIds = [701, 702, 703, 704, 705, 706, 707, 708];
  const berryQuests = filteredData.filter(
    (q: QuestStat) => q.reward_type === 2 && q.reward_item_id && berryIds.includes(q.reward_item_id)
  );

  const otherItemQuests = filteredData.filter((q: QuestStat) => {
    if (q.reward_type === 7 || q.reward_type === 3) return false;
    if (q.reward_type === 2 && q.reward_item_id && (pokeballIds.includes(q.reward_item_id) || berryIds.includes(q.reward_item_id))) return false;
    return true;
  });

  const renderTable = (quests: QuestStat[], title: string, _isPokemonTable: boolean) => {
    if (quests.length === 0) return null;

    return (
      <div className="flex flex-col gap-3 sm:gap-4">
        <h3 className="text-pory-blue-900 text-lg sm:text-xl font-black tracking-tight uppercase italic px-3 sm:px-0 sm:ml-2">
          {title}
        </h3>
        <Card className="shadow-none sm:shadow-xl overflow-hidden border-y sm:border border-slate-200 rounded-none sm:rounded-xl bg-white pt-0">
          <div className="flex flex-col">
            <div className="bg-pory-blue-50/50 border-pory-blue-100 hidden grid-cols-[80px_1fr_100px_120px_40px] items-center border-b px-6 py-4 sm:grid">
              <div className="text-pory-blue-900 text-xs font-black tracking-wider uppercase italic">
                {tCommon("reward")}
              </div>
              <div className="text-pory-blue-900 text-xs font-black tracking-wider uppercase italic">
                {t("task")}
              </div>
              <div className="text-pory-blue-900 text-xs font-black tracking-wider uppercase italic">
                {tCommon("lastSeen")}
              </div>
              <div className="text-pory-blue-900 text-right text-xs font-black tracking-wider uppercase italic">
                {t("frequency")}
              </div>
              <div></div>
            </div>

            <div className="flex flex-col">
              {quests.map((q: QuestStat, i: number) => {
                const task = q.translated_task;
                const rewardText = q.translated_reward;
                const count = q.quest_count ?? 1;
                const percentage = data.totalQuests > 0 ? ((count / data.totalQuests) * 100).toFixed(1) : "0.0";
                
                const isPokemon = q.reward_type === 7;
                const questSlug = resolveQuestUrlSlug({
                  taskSlug: q.task_slug,
                  translatedTitle: task,
                  title: q.title,
                  target: q.quest_target,
                  locale,
                });
                const hasValidSlug = isValidSlug(questSlug);

                const pokeUrl = isPokemon && q.reward_pokemon_id ? getPokemonUrl(
                  q.reward_pokemon_id,
                  q.reward_pokemon_form_id ?? 0,
                  q.reward_pokemon_costume_id ?? 0,
                ) : null;

                const imgUrl = q.image_url;

                return (
                  <div
                    key={`${q.reward_pokemon_id}-${q.reward_item_id}-${i}`}
                    className="group hover:bg-pory-blue-50/30 border-pory-blue-50/30 grid grid-cols-[50px_1fr_80px] items-center border-b px-3 py-3 transition-colors last:border-0 sm:grid-cols-[80px_1fr_100px_120px_40px] sm:px-6 sm:py-4"
                  >
                    <div>
                      {pokeUrl ? (
                        <Link href={pokeUrl} className="block relative h-10 w-10 sm:h-12 sm:w-12 transition-transform group-hover:scale-110">
                          {imgUrl ? (
                            <Image
                              src={getPokemonImageSrc(imgUrl, false, null)}
                              alt={rewardText}
                              fill
                              sizes="48px"
                              className="object-contain"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-neutral-100 rounded-full text-neutral-500">
                              <Gift className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                            </div>
                          )}
                        </Link>
                      ) : (
                        <div className="relative h-10 w-10 sm:h-12 sm:w-12 transition-transform group-hover:scale-110">
                          {imgUrl ? (
                            <Image
                              src={getPokemonImageSrc(imgUrl, false, null)}
                              alt={rewardText}
                              fill
                              sizes="48px"
                              className="object-contain"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center bg-neutral-100 rounded-full text-neutral-500">
                              <Gift className="h-5 w-5 sm:h-6 sm:w-6" aria-hidden="true" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="text-pory-blue-900 pr-2 sm:pr-4 font-bold min-w-0">
                      <div className="flex flex-col">
                        {hasValidSlug ? (
                          <Link
                            href={`/quests/${questSlug}`}
                            className="truncate text-sm sm:text-lg hover:text-pory-blue-600 hover:underline transition-colors"
                          >
                            {task}
                          </Link>
                        ) : (
                          <span className="truncate text-sm sm:text-lg">
                            {task}
                          </span>
                        )}
                        {pokeUrl ? (
                          <Link
                            href={pokeUrl}
                            className="text-[11px] sm:text-xs text-neutral-600 hover:text-pory-pink-600 hover:underline uppercase transition-colors truncate"
                          >
                            {tCommon("reward")}: {rewardText}
                          </Link>
                        ) : (
                          <span className="text-[11px] sm:text-xs text-neutral-600 uppercase truncate">
                            {tCommon("reward")}: {rewardText}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="hidden text-sm font-medium text-neutral-700 sm:block">
                      {formatTimeAgo(q.last_seen, tCommon)}
                    </div>
                    <div className="text-pory-blue-500 text-right font-black tabular-nums">
                      <div className="text-xs sm:text-base">
                        {percentage}%
                      </div>
                      <div className="text-[10px] sm:text-xs font-bold tracking-tighter text-neutral-600 uppercase sm:tracking-normal">
                        {t("totalCount", { count: count.toLocaleString() })}
                      </div>
                    </div>
                    <div className="hidden justify-end sm:flex">
                      {hasValidSlug ? (
                        <Link
                          href={`/quests/${questSlug}`}
                          className="p-1 rounded-full text-neutral-400 hover:text-pory-blue-600 hover:bg-pory-blue-50 transition-colors"
                          aria-label={`View details for ${task}`}
                        >
                          <ArrowRight className="h-5 w-5" aria-hidden="true" />
                        </Link>
                      ) : (
                        <div className="w-7 h-7" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-12">
      <div className="flex flex-col gap-6 sm:gap-10 lg:col-span-12">
        <div className="px-3 sm:px-0 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <h2 className="text-pory-blue-900 text-xl sm:text-2xl font-black tracking-tight uppercase italic">
              {t("liveFeed")}
            </h2>
            <span className="text-pory-blue-500 bg-pory-blue-50 border-pory-blue-100 rounded-full border px-3 py-1 text-xs sm:text-sm font-bold animate-pulse">
              {t("liveUpdate")}
            </span>
          </div>
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 h-4 w-4" aria-hidden="true" />
            <input 
              id="search-quests"
              type="text" 
              placeholder={t("searchPlaceholder")}
              aria-label={t("searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-neutral-300 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-pory-blue-300 focus:border-transparent text-sm bg-white placeholder:text-neutral-500"
            />
          </div>
        </div>

        {data.quests.length === 0 && (
          <div className="flex items-center justify-center py-20 text-neutral-500 text-xl font-medium">
            {t("noQuests")}
          </div>
        )}

        {renderTable(pokemonQuests, t("pokemonEncounters"), true)}
        {renderTable(stardustQuests, t("stardustRewards"), false)}
        {renderTable(pokeballQuests, t("pokeballs"), false)}
        {renderTable(berryQuests, t("berries"), false)}
        {renderTable(otherItemQuests, t("otherItems"), false)}
      </div>
    </div>
  );
}
